/**
 * Provides the `typeWriter` utility function for simulating a typewriter effect, typing a given text string into an HTML input or textarea field character by character with a configurable delay.
 * @module typeWriter
 */
// Default delay for typing characters in milliseconds
const DEFAULT_TYPEWRITER_DELAY = 50;
/**
 * Internal helper function that recursively types text into an input field character by character.
 * It includes input validation and error handling.
 * This function is not intended for direct external use; use the `typeWriter` wrapper instead.
 *
 * @since 5.9 Initial implementation.
 * @param {TypeWriterHelperOptions} options - The options for the helper function.
 * @param {TypeWriterFieldElement} options.$field - The HTML input or textarea element where text will be typed.
 * @param {string} options.text - The full string to be typed.
 * @param {number} options.index - The current character index in the `text` string to be typed next.
 * @param {number} options.delay - The delay in milliseconds between typing each character.
 * @param {() => void} options.resolve - The resolve function of the Promise.
 * @param {(reason?: any) => void} options.reject - The reject function of the Promise.
 * @internal
 */
function typeWriterHelper({ $field, text, index, delay, resolve, reject }) {
    try {
        // Validate the $field parameter
        if (!($field instanceof HTMLElement && 'value' in $field)) {
            throw new TypeError('typeWriterHelper: Invalid field element provided. Must be an HTMLElement with a value property.');
        }
        // Validate the text parameter
        if (typeof text !== 'string') {
            throw new TypeError('typeWriterHelper: Text must be a string.');
        }
        // Validate the index parameter
        if (typeof index !== 'number' || !Number.isInteger(index) || index < 0) {
            throw new TypeError('typeWriterHelper: Index must be a non-negative integer.');
        }
        // Validate the delay parameter
        if (typeof delay !== 'number' || delay < 0) {
            throw new TypeError('typeWriterHelper: Delay must be a non-negative number.');
        }
        // Check if there are more characters to type
        if (index < text.length) {
            // Append the next character to the input field's value
            $field.value += text[index];
            // Call typeWriterHelper recursively with the next index after the specified delay
            setTimeout(() => typeWriterHelper({ $field, text, index: index + 1, delay, resolve, reject }), delay);
        }
        else {
            // If typing is complete (all characters typed), resolve the Promise.
            resolve();
        }
    }
    catch (error) {
        // Log any errors that occur during validation or execution to the console.
        // Ensure error is an instance of Error to access message property safely.
        let errorMessage = 'An unknown error occurred in typeWriterHelper';
        if (error instanceof Error) {
            errorMessage = `An error occurred in typeWriterHelper: ${error.message}`;
        }
        else if (typeof error === 'string') {
            errorMessage = `An error occurred in typeWriterHelper: ${error}`;
        }
        console.error(errorMessage, error); // Log the full error object as well for more context
        reject(new Error(errorMessage)); // Reject the promise with the error
    }
}
;
/**
 * Simulates a typewriter effect, typing a given text string into an HTML input or textarea field character by character with a configurable delay. Returns a Promise that resolves when typing is complete.
 *
 * **Behavior:**
 * 1. **Input:** Takes an HTML field element (`$field`), the `text` string to type, and an optional `options` object which can include a `delay`.
 * 2. **Promise-based:** Returns a `Promise<void>` that resolves when the entire text has been typed successfully, or rejects if an error occurs during validation or execution.
 * 3. **Initialization:** Clears the existing value of the `$field` before starting the typing effect.
 * 4. **Typing simulation:**
 *    - Uses an internal helper function (`typeWriterHelper`) that recursively appends one character from the `text` to the `$field.value` at a time.
 *    - A `setTimeout` introduces a delay (configurable via `options.delay`, defaulting to 50ms) between typing each character.
 * 5. **Error handling:** Input validation errors or other exceptions within the helper will cause the returned Promise to reject. Errors are logged to the console.
 *
 * **Demo:**
 *
 * <video controls width="100%" style="max-width: 100%;">
 * <source src="https://design.uncannycloud.com/wp-content/uploads/2025/05/docs__typewriter-demo.mp4" type="video/mp4">
 * </video>
 *
 * @param {TypeWriterFieldElement} $field - The HTML input or textarea element into which the text will be typed.
 * @param {string} text - The string of text to be typed out.
 * @param {TypeWriterOptions} [options] - Optional. An object containing configuration options.
 * @param {number} [options.delay=50] - Delay in milliseconds between each character.
 * @return {Promise<void>} A Promise that resolves when typing is complete, or rejects on error.
 *
 * @example Importing the typeWriter animation
 * ```typescript
 * // Import global animations
 * import { typeWriter } from '@automator/shared/animations/typewriter';
 * ```
 *
 * @example Using typewriter effect in a component method
 * Demonstrates calling `typeWriter` within an async method of a component-like class.
 * ```typescript
 * @customElement( 'uo-component' )
 * export class AutomatorComponent extends LitElement
 *    // ...
 *
 *    public async updateTitle( newTitle: string ): Promise<void> {
 *        try {
 *            // Assumes this.$titleField: HTMLInputElement | HTMLTextAreaElement
 *            await typeWriter( this.$titleField, newTitle );
 *            this.title = newTitle; // Update internal state after animation completes
 *            console.log( `Title updated to: "${ this.title }"` );
 *        } catch ( error ) {
 *            console.error( `Failed to update title with typewriter effect:`, error);
 *            // Optionally re-throw or handle as specific to the component's needs
 *            throw error;
 *        }
 *    }
 * }
 *
 * // How it might be used:
 * // this.updateTitle( 'New title' )
 * // 	.then( () => console.log( 'Completed!' ) )
 * // 	.catch( e => console.error( 'Failed:', e ) );
 * ```
 *
 * @example Handling different fields and options
 * ```typescript
 * // Assumes this.$titleField: HTMLInputElement | HTMLTextAreaElement
 * await typeWriter( this.$titleField, 'Title', { delay: 100 } );
 * ```
 *
 * @remarks
 * - This function directly manipulates the `.value` property of the provided HTML field element.
 * - The typing speed is controlled by the `delay` option.
 * - Input validation is performed by `typeWriterHelper`. If invalid parameters are passed, the Promise will reject.
 * - For comprehensive behavior details, including timing and error handling, refer to the unit tests.
 */
export function typeWriter($field, text, options) {
    return new Promise((resolve, reject) => {
        // Destructure options with default values
        const { delay = DEFAULT_TYPEWRITER_DELAY, // Default delay
         } = options || {}; // Use empty object if options is undefined
        // Clear the field's value before starting the typewriter effect
        // Perform this check before starting the helper to avoid partial typing on invalid field
        if (!($field instanceof HTMLElement && 'value' in $field)) {
            // Match the error message from the helper for consistency if caught early
            const error = new TypeError('typeWriterHelper: Invalid field element provided. Must be an HTMLElement with a value property.');
            console.error(error.message); // Log the error directly
            reject(error);
            return;
        }
        $field.value = '';
        // Start the recursive typing process from the first character (index 0).
        // Pass resolve and reject to the helper.
        typeWriterHelper({ $field, text, index: 0, delay, resolve, reject });
    });
}
;
